import React from "react";
import Radium from "radium";
import Helmet from "react-helmet";
import {
  Card,
  CardMedia,
  CardTitle,
  CardText,
  CardActions,
  RaisedButton,
  FlatButton,
  CircularProgress,
  Dialog
} from "material-ui";

import FilmButton from "./partials/film-button.jsx";
import FilmCard from "./partials/film-card.jsx";
import FullPageBackground from "./partials/full-page-background.jsx";
import SocialShareWidget from "./partials/social-share-widget.jsx";

import * as util from "../util";

import { filterEventTagsByPass } from "../utils/TagUtil.js";

import connectToStores from "alt-utils/lib/connectToStores";
import FilmStore from "../stores/FilmStore";
import PassStore from "../stores/PassStore";
import PersonStore from "../stores/PersonStore";
import PassActions from "../actions/PassActions";
import FilmActions from "../actions/FilmActions";
import EventActions from "../actions/EventActions";
import AppUtil from "../utils/AppUtil.js";

export const DETAILS = {
  year: "Year",
  runtime: "Runtime (min)",
  language: "Language",
  country: "Country",
  premiere: "Premiere",
  rating: "Rating",
  genre: "Genre",
  subtitle_language: "Subtitle Language",
  content_warning: "Content Warning",
  sponsor: "Sponsor",
  awards: "Awards",
  note: "Note",
  social_media: "Social Media"
};

export const CREDITS = {
  director: "Director",
  screenwriter: "Screenwriter",
  producer: "Producer",
  executive_producer: "Executive Producer",
  co_producer: "Co-Producer",
  filmmaker: "Filmmaker",
  cast: "Cast",
  cinematographer: "Cinematographer",
  editor: "Editor",
  animator: "Animator",
  production_design: "Production Design",
  composer: "Composer",
  sound_design: "Sound Design",
  music: "Music"
};

class Film extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      fetchedFilm: null
    };

    this.back = this.back.bind(this);
  }

  static getStores() {
    return [FilmStore, PassStore];
  }

  static getPropsFromStores() {
    return {
      getFilmById: FilmStore.getFilmById
    };
  }

  static get contextTypes() {
    return {
      router: React.PropTypes.object.isRequired,
      tenant: React.PropTypes.object,
      isLarge: React.PropTypes.bool
    };
  }

  componentDidMount() {
    if (this.context.tenant.marquee_enabled) {
      EventActions.fetchMarquee({ firstTimeOnly: true });
    }

    if (PersonStore.isLoggedIn()) {
      PassActions.fetchAll.defer({ firstTimeOnly: true });
    }

    this.componentWillReceiveProps(this.props);
  }

  componentWillReceiveProps(props) {
    let { filmId } = props.params;
    if (this.state.fetchedFilm !== filmId) {
      this.setState({ fetchedFilm: filmId });
      FilmActions.fetchOne(filmId);
      EventActions.fetchAllForFilm(filmId);
    }
  }

  back(toWhat, forceImmediate) {
    // Timeout delay if on mobile
    if (!forceImmediate && !this.context.isLarge) {
      setTimeout(() => this.back(toWhat, true), 150);
      return;
    }

    if (toWhat === "guide") {
      if (this.props.location.state && this.props.location.state.fromGuide) {
        this.context.router.goBack();
      } else {
        this.context.router.push("/films");
      }
    } else if (toWhat === "event") {
      this.context.router.goBack();
    }
  }

  getStyles() {
    return {
      card: {
        borderRadius: "0",
        overflow: "visible",
        position: "relative"
      },
      bannerPaddedCard: {
        marginTop: 10
      },
      cardSmall: {
        boxShadow: "none"
      },
      backToGuide: {
        position: "absolute",
        left: 16,
        top: 16,
        zIndex: 1
      },
      imageWrapper: {
        overflow: "hidden",
        position: "relative",
        backgroundColor: "#ccc"
      },
      stillWrapper: {
        paddingTop: "56.25%"
      },
      overlay: {
        height: "100%",
        textAlign: "center",
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center"
      },
      poster: {
        maxHeight: 240,
        borderRadius: 5,
        boxShadow: "0 1rem 2.1rem rgba(69, 77, 93, 0.3)"
      },
      title: {
        fontSize: "2em",
        margin: "0.7em 0 0.25em",
        fontWeight: "bold"
      },
      subtitle: {
        fontSize: "1em",
        fontStyle: "italic"
      },
      cardText: {
        minHeight: 200,
        fontSize: 15,
        lineHeight: "22px",
        color: "rgb(62, 62, 62)"
      },
      socialShare: {
        marginTop: 20
      }
    };
  }

  render() {
    let film = this.props.getFilmById(this.props.params.filmId);

    if (!film) {
      return (
        <div style={{ padding: 10, textAlign: "center" }}>
          <CircularProgress
            mode="indeterminate"
            size={119}
            thickness={7}
            style={{ margin: 10.5 }}
          />
        </div>
      );
    }

    let styles = this.getStyles(),
      fromEvent =
        this.props.location.state && this.props.location.state.fromEvent,
      fromSchedule =
        this.props.location.state && this.props.location.state.fromSchedule,
      stillsMode = this.context.tenant.film_guide_stills_mode,
      cover = stillsMode ? film.still_image : film.cover_image,
      hasStill = stillsMode && cover,
      alternateDisplayMode =
        this.context.tenant.film_alternate_display_mode && cover;

    // filter the events by tags and passes
    let filmEvents = film.events;
    let passes = PassStore.getPassesForCurrentEventBucket();
    if (this.context.tenant.virtual_protected_tag_list && filmEvents) {
      let passList = Object.values(passes || {});
      filmEvents = filterEventTagsByPass(
        film.events,
        this.context.tenant.virtual_protected_tag_list,
        passList
      );
    }

    return (
      <div className="Eventive--FilmPage">
        <Helmet
          title={film.name}
          meta={[
            { name: "og:title", content: film.name },
            { name: "og:description", content: film.short_description },
            { name: "og:image", content: cover }
          ]}
        />
        {this.props.children
          ? React.cloneElement(this.props.children, {
              appSettings: this.props.appSettings
            })
          : null}
        {/*this.context.isLarge && <FullPageBackground src={film.cover_image} />*/}
        <Card
          className="FilmPage"
          style={Object.assign(
            {},
            styles.card,
            !this.context.isLarge && styles.cardSmall,
            this.context.isLarge &&
              this.context.tenant.add_event_banner_padding &&
              styles.bannerPaddedCard
          )}
        >
          {!AppUtil.isSundanceMobileMode(this.context.tenant) ? (
            <div style={styles.backToGuide}>
              {fromEvent || fromSchedule ? (
                <RaisedButton
                  label={
                    <span>
                      <span style={{ fontSize: "1.4em", verticalAlign: -1 }}>
                        ‹
                      </span>{" "}
                      {fromSchedule ? "schedule" : "event"}
                    </span>
                  }
                  style={{ verticalAlign: "top", marginRight: 7 }}
                  onClick={() => this.back("event")}
                />
              ) : null}
              <RaisedButton
                label={
                  <span>
                    {!fromEvent && !fromSchedule && (
                      <span style={{ fontSize: "1.4em", verticalAlign: -1 }}>
                        ‹
                      </span>
                    )}{" "}
                    {this.context.tenant.display_film_guide}
                  </span>
                }
                style={{ verticalAlign: "top", marginRight: 7 }}
                onClick={() => this.back("guide")}
              />
            </div>
          ) : null}
          <CardMedia
            overlay={
              alternateDisplayMode ? null : (
                <div style={styles.overlay}>
                  {!this.context.tenant.film_guide_hide_poster ? (
                    <img src={film.poster_image} style={styles.poster} />
                  ) : null}
                  <div style={styles.title}>{film.name}</div>
                  <div style={styles.subtitle}>
                    {util.makeFilmDetailString(film.details)}
                  </div>
                </div>
              )
            }
            overlayContentStyle={{ top: 0 }}
            style={styles.media}
          >
            <div
              style={Object.assign(
                {},
                styles.imageWrapper,
                hasStill && styles.stillWrapper,
                !hasStill && {
                  height: cover || (!hasStill && film.poster_image) ? 380 : 176
                },
                cover && {
                  backgroundImage: `url(${cover})`,
                  backgroundPosition: "center",
                  backgroundSize: "cover"
                }
              )}
            />
          </CardMedia>
          <CardText style={styles.cardText}>
            {!this.context.tenant.marquee_enabled && (
              <FilmButton events={filmEvents || []} filmId={film.id} />
            )}
            <FilmCard
              film={film}
              hideTitle={!alternateDisplayMode}
              hidePoster={true}
              eventLinkState={{ from: "film" }}
              passes={passes}
            />

            {!AppUtil.isSundanceMobileMode(this.context.tenant) ? (
              <SocialShareWidget style={styles.socialShare} />
            ) : null}
          </CardText>
        </Card>
      </div>
    );
  }
}

export default Radium(
  util.slugify(FilmStore.getFilmById, "filmId")(connectToStores(Film))
);



// WEBPACK FOOTER //
// ./src/app/components/film.jsx